import { useMemo } from 'react';
import dayjs from 'dayjs';
import { IntlShape, useIntl } from 'react-intl';
import { Schemas } from '../../../../../../../../../openapi/generated/types';

import {
  ReservationFlowSelectedPaymentMethod,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
  ReservationFlowPaymentMethodListSubscription,
  ReservationFlowCustomerSubscriptionAvailableReservationCount,
} from 'src/interfaces/reservation_flow';
import { isAvailableSubscriptionProduct } from 'src/state-machines/booking/functions/is-available-subscription-product';
import { getDayWithSuffix } from 'src/utils/intl';

export interface UseDisplayBehaviorParams {
  value?:
    | ReservationFlowSelectedPaymentMethod
    | ReservationFlowSelectedPaymentMethodNewCreditCard;
  merchantTimeZone: string;
  paymentMethodsSubscription: ReservationFlowPaymentMethodListSubscription;
  customerSubscriptionAvailableReservationCounts?: ReservationFlowCustomerSubscriptionAvailableReservationCount[];
  selectedDate?: string; // 現時点では複数日程に対応しない
}

export interface UseDisplayBehaviorValue {
  items: {
    product: ReservationFlowPaymentMethodListSubscription['products'][number];
    isSelected: boolean;
    isDisabled: boolean;
    notes?: { text: string; isWarning: boolean; details?: [] }[];
  }[];
}

export const useDisplayBehavior: (
  params: UseDisplayBehaviorParams,
) => UseDisplayBehaviorValue = ({
  value,
  merchantTimeZone,
  paymentMethodsSubscription,
  customerSubscriptionAvailableReservationCounts,
  selectedDate,
}) => {
  const intl = useIntl();

  const items = useMemo<UseDisplayBehaviorValue['items']>(() => {
    // 有効な月謝のみを表示する
    const listedProducts = paymentMethodsSubscription.products.filter(
      (product) => {
        return product.active_customer_subscriptions.length > 0;
      },
    );

    return listedProducts.map((product) => {
      const activeCustomerSubscription =
        product.active_customer_subscriptions[0];

      const isSelected = (() => {
        if (typeof value === 'undefined') {
          return false;
        }

        if (value.type !== 'subscription') {
          return false;
        }

        return (
          value.customer_subscription_public_id ===
          activeCustomerSubscription.public_id
        );
      })();

      // 日時選択と予約回数制限の取得が完了するまでは選択不可
      if (
        typeof selectedDate === 'undefined' ||
        typeof customerSubscriptionAvailableReservationCounts === 'undefined'
      ) {
        return {
          product,
          isSelected,
          isDisabled: true,
        };
      }

      const availableReservationCount =
        customerSubscriptionAvailableReservationCounts.find((count) => {
          return count.publicId === activeCustomerSubscription.public_id;
        });

      // 対応する予約回数制限が取得できていない場合は選択不可とする
      if (typeof availableReservationCount === 'undefined') {
        return {
          product,
          isSelected,
          isDisabled: true,
          notes: [
            {
              text: intl.formatMessage({
                defaultMessage: 'この月謝は利用できません',
                id: 'bDx1AI',
                description:
                  'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/hooks/useDisplayBehavior/index.ts',
              }),
              isWarning: true,
            },
          ],
        };
      }

      const isDisabled = !isAvailableSubscriptionProduct({
        product,
        availableReservationCounts:
          customerSubscriptionAvailableReservationCounts,
      });

      const notes = getNotes(
        {
          merchantTimeZone,
          availableReservationCount,
          selectedDate,
        },
        intl,
      );

      return {
        product,
        isSelected,
        isDisabled,
        notes,
      };
    });
  }, [
    value,
    merchantTimeZone,
    paymentMethodsSubscription,
    customerSubscriptionAvailableReservationCounts,
    selectedDate,
    intl,
  ]);

  return {
    items,
  };
};

export const getNotes: (
  params: {
    merchantTimeZone: string;
    availableReservationCount: ReservationFlowCustomerSubscriptionAvailableReservationCount;
    selectedDate: string;
  },
  intl: IntlShape,
) => { text: string; isWarning: boolean }[] = (
  { merchantTimeZone, availableReservationCount, selectedDate },
  intl,
) => {
  return [
    getMonthlyNote(
      {
        merchantTimeZone,
        details: availableReservationCount.value.details,
        selectedDate,
      },
      intl,
    ),
    getWeeklyNote(
      {
        merchantTimeZone,
        count: availableReservationCount.value.weekly,
        selectedDate,
      },
      intl,
    ),
    getDailyNote(
      {
        merchantTimeZone,
        count: availableReservationCount.value.daily,
        selectedDate,
      },
      intl,
    ),
    getUpcomingNote(
      {
        count: availableReservationCount.value.upcoming,
      },
      intl,
    ),
  ].filter(Boolean) as { text: string; isWarning: boolean }[];
};

export const getMonthlyNote: (
  params: {
    merchantTimeZone: string;
    details: Schemas.CustomerSubscriptionAvailableCount[];
    selectedDate: string;
  },
  intl: IntlShape,
) => { text: string; isWarning: boolean; details?: string[] } | undefined = (
  { merchantTimeZone, details, selectedDate },
  intl,
) => {
  const total = details
    .map(({ quantity: { available } }) => available ?? Infinity)
    .reduce((acc, available) => acc + available, 0);

  if (total === Infinity) {
    return;
  }

  const dateText = intl.formatDate(selectedDate, {
    timeZone: merchantTimeZone,
    month: 'long',
  });

  if (total === 0) {
    return {
      text: intl.formatMessage(
        {
          defaultMessage:
            '当月（{dateText}）：予約回数の上限に達したため利用できません',
          id: 'sN5Jad',
          description:
            'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/hooks/useDisplayBehavior/index.ts',
        },
        {
          dateText,
        },
      ),
      isWarning: true,
    };
  }

  const hasCarryOver = details
    .filter(({ type }) => type === 'carry_over')
    .map(({ quantity: { available } }) => available ?? Infinity)
    .some((available) => available ?? Infinity > 0);

  return {
    text: intl.formatMessage(
      {
        defaultMessage: '当月（{dateText}）：あと{count}回',
        id: '/xHSNm',
        description:
          'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/hooks/useDisplayBehavior/index.ts',
      },
      {
        dateText,
        count: total,
      },
    ),
    isWarning: false,
    details: hasCarryOver
      ? details
          .filter(({ quantity: { available } }) => (available ?? Infinity) > 0)
          .map((detail) =>
            intl.formatMessage(
              {
                defaultMessage:
                  '{type, select, this_month {当月分} carry_over {繰越分} other {-}}：あと{available}回{expires_at, select, undefined {} other {（有効期限：{expires_at}）}}',
                id: 'Nf6aj3',
                description:
                  'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/hooks/useDisplayBehavior/index.ts',
              },
              {
                type: detail.type,
                available: detail.quantity.available,
                expires_at:
                  detail.type === 'carry_over'
                    ? dayjs(detail.expires_at)
                        .tz(merchantTimeZone)
                        .format(
                          intl.formatMessage({
                            defaultMessage: 'YYYY年MM月DD日',
                            id: '2v54pV',
                            description:
                              'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/hooks/useDisplayBehavior/index.ts',
                          }),
                        )
                    : undefined,
              },
            ),
          )
      : undefined,
  };
};

export const getWeeklyNote: (
  params: {
    merchantTimeZone: string;
    count: number | null;
    selectedDate: string;
  },
  intl: IntlShape,
) => { text: string; isWarning: boolean } | undefined = (
  { merchantTimeZone, count, selectedDate },
  intl,
) => {
  if (count === null) {
    return undefined;
  }

  const startDay = dayjs
    .tz(selectedDate, 'YYYY-MM-DD', merchantTimeZone)
    .startOf('isoWeek');
  const endDay = dayjs
    .tz(selectedDate, 'YYYY-MM-DD', merchantTimeZone)
    .endOf('isoWeek');

  const isNextMondayIsNextMonth = startDay.get('month') !== endDay.get('month');

  const dateText = `${intl.formatDate(startDay.toDate(), {
    timeZone: merchantTimeZone,
    dateStyle: 'long',
  })}~${
    isNextMondayIsNextMonth
      ? intl.formatDate(endDay.toDate(), {
          timeZone: merchantTimeZone,
          month: 'long',
          day: 'numeric',
        })
      : getDayWithSuffix(endDay.get('date'), intl)
  }`;

  if (count === 0) {
    return {
      text: intl.formatMessage(
        {
          defaultMessage:
            '当週（{dateText}）：予約回数の上限に達したため利用できません',
          id: 'I7OhmM',
          description:
            'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/hooks/useDisplayBehavior/index.ts',
        },
        {
          dateText,
        },
      ),
      isWarning: true,
    };
  }

  return {
    text: intl.formatMessage(
      {
        defaultMessage: '当週（{dateText}）：あと{count}回',
        id: 'bpUAWv',
        description:
          'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/hooks/useDisplayBehavior/index.ts',
      },
      {
        dateText,
        count,
      },
    ),
    isWarning: false,
  };
};

export const getDailyNote: (
  params: {
    merchantTimeZone: string;
    count: number | null;
    selectedDate: string;
  },
  intl: IntlShape,
) => { text: string; isWarning: boolean } | undefined = (
  { merchantTimeZone, count, selectedDate },
  intl,
) => {
  if (count === null) {
    return undefined;
  }

  const dateText = intl.formatDate(selectedDate, {
    timeZone: merchantTimeZone,
    month: 'long',
    day: 'numeric',
  });

  if (count === 0) {
    return {
      text: intl.formatMessage(
        {
          defaultMessage:
            '当日（{dateText}）：予約回数の上限に達したため利用できません',
          id: 'r661lK',
          description:
            'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/hooks/useDisplayBehavior/index.ts',
        },
        {
          dateText,
        },
      ),
      isWarning: true,
    };
  }

  return {
    text: intl.formatMessage(
      {
        defaultMessage: '当日（{dateText}）：あと{count}回',
        id: '9pn/nB',
        description:
          'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/hooks/useDisplayBehavior/index.ts',
      },
      {
        dateText,
        count,
      },
    ),
    isWarning: false,
  };
};

export const getUpcomingNote: (
  params: {
    count: number | null;
  },
  intl: IntlShape,
) => { text: string; isWarning: boolean } | undefined = ({ count }, intl) => {
  if (count === null) {
    return undefined;
  }

  if (count === 0) {
    return {
      text: intl.formatMessage({
        defaultMessage:
          '同時に持てる予約数：上限に達しているため利用できません',
        id: 'jp6bM8',
        description:
          'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/hooks/useDisplayBehavior/index.ts',
      }),
      isWarning: true,
    };
  }

  return {
    text: intl.formatMessage(
      {
        defaultMessage: '同時に持てる予約数：あと{count}回',
        id: 'Jz9gIF',
        description:
          'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/hooks/useDisplayBehavior/index.ts',
      },
      {
        count,
      },
    ),
    isWarning: false,
  };
};
